import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

/**
 * A custom hook to manage query parameters in the URL.
 * @returns {object} - Methods to add, remove, and set query parameters.
 */
export const useQueryParams = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  /**
   * Adds or updates a query parameter in the URL.
   * @param key - The key of the query parameter.
   * @param value - The value of the query parameter.
   */
  const addQueryParam = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams ?? {});
      params.set(key, value);
      void router.replace(`${pathname}?${params.toString()}`, undefined, {
        scroll: false,
        shallow: true,
      });
    },
    [router, pathname, searchParams],
  );
  /**
   * Removes a query parameter from the URL.
   * @param {string} key - The key of the query parameter to remove.
   */
  const removeQueryParam = useCallback(
    async (key: string) => {
      const params = new URLSearchParams(searchParams ?? {});
      params.delete(key);
      const paramsString = params.toString();
      await router.replace(
        `${pathname}${paramsString ? `?${paramsString}` : ''}`,
        undefined,
        {
          scroll: false,
          shallow: true,
        },
      );
    },
    [router, pathname, searchParams],
  );

  /**
   * Replaces the query parameters with the provided object.
   * @param {object} newParams - An object representing the new query parameters.
   */
  const setQueryParams = useCallback(
    (newParams: Record<string, string>) => {
      const params = new URLSearchParams(newParams);
      const paramsString = params.toString();
      void router.replace(
        `${pathname}${paramsString ? `?${paramsString}` : ''}`,
        undefined,
        {
          scroll: false,
          shallow: true,
        },
      );
    },
    [router, pathname],
  );

  return { addQueryParam, removeQueryParam, setQueryParams };
};
